import React from 'react';
import { CheckCircle, Clock, Target, TrendingUp } from 'lucide-react';
import { useParentPortal } from '../contexts/ParentPortalContext';

const ProgressIndicator = ({ student, phase }) => {
  const { studentProgress } = useParentPortal();

  if (!student || !studentProgress) {
    return null;
  }

  const { completedMilestones, totalMilestones, colleges, essays, hasAssessment } = studentProgress;
  const progressPercentage = Math.round((completedMilestones / totalMilestones) * 100);

  // Phase-specific messaging
  const getPhaseInfo = (phase) => {
    switch (phase) {
      case 'discovery':
        return {
          title: 'Discovery Phase',
          description: 'Building self-awareness and exploring possibilities',
          color: 'indigo',
          icon: Target
        };
      case 'planning':
        return {
          title: 'Strategic Planning',
          description: 'Developing college strategy and building applications',
          color: 'teal',
          icon: TrendingUp
        };
      case 'execution':
        return {
          title: 'Application Season',
          description: 'Submitting applications and making final decisions',
          color: 'azure',
          icon: CheckCircle
        };
      default:
        return {
          title: 'College Journey',
          description: 'Supporting your student\'s path to college',
          color: 'slate',
          icon: Clock
        };
    }
  };

  const phaseInfo = getPhaseInfo(phase);
  const Icon = phaseInfo.icon;

  return (
    <div className="bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Left side - Student progress overview */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 bg-${phaseInfo.color}-100 rounded-lg flex items-center justify-center`}>
                <Icon className={`w-5 h-5 text-${phaseInfo.color}-600`} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">
                  {student.name || student.displayName}'s {phaseInfo.title}
                </h3>
                <p className="text-sm text-slate-600">
                  {phaseInfo.description}
                </p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="flex items-center gap-3">
              <div className="w-32 h-2 bg-stone-200 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r from-${phaseInfo.color}-500 to-${phaseInfo.color}-600 transition-all duration-500`}
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <span className="text-sm font-medium text-slate-700">
                {progressPercentage}% Complete
              </span>
            </div>
          </div>

          {/* Right side - Key metrics */}
          <div className="flex items-center gap-6">
            {hasAssessment && (
              <div className="text-center">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-1">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="text-xs text-slate-600">Assessment</div>
                <div className="text-sm font-medium text-emerald-700">Complete</div>
              </div>
            )}

            <div className="text-center">
              <div className="text-lg font-bold text-slate-900">{colleges.length}</div>
              <div className="text-xs text-slate-600">Colleges</div>
            </div>

            <div className="text-center">
              <div className="text-lg font-bold text-slate-900">{essays.length}</div>
              <div className="text-xs text-slate-600">Essays</div>
            </div>

            <div className="text-center">
              <div className="text-lg font-bold text-slate-900">
                {completedMilestones}/{totalMilestones}
              </div>
              <div className="text-xs text-slate-600">Milestones</div>
            </div>
          </div>
        </div>

        {/* Confidence-building message */}
        <div className="mt-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
          <p className="text-sm text-emerald-800">
            <strong>You're providing excellent support!</strong> {student.name} is making steady progress,
            and your strategic guidance is helping them stay on track for college success.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProgressIndicator;